import React, { useState, useEffect } from "react";
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Typography,
  Chip,
  Box,
  IconButton,
  Pagination,
  InputAdornment,
  TextField,
  Tooltip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import ArticleIcon from "@mui/icons-material/Article";
import EditIcon from "@mui/icons-material/Edit";
import BlockIcon from "@mui/icons-material/Block";
import SearchIcon from "@mui/icons-material/Search";
import Swal from "sweetalert2";
import { getNoticiasEventos, updateNoticiaEvento } from "../services/noticiasEventosService";

const rowsPerPage = 5;

const getNoticiaEventoId = (item) =>
  item?.id ?? item?.Id ?? item?.idNoticiaEvento ?? item?.IdNoticiaEvento ?? item?.idNoticiasEventos ?? item?.idnoticiaseventos;

const estadoLabel = (estado) => estado === "Publicado" ? "Publicado" : estado === "Borrado" ? "Borrado" : "Borrador";

export default function TableNoticiasEventos() {
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchItems = async () => {
    try {
      setLoading(true);
      const data = await getNoticiasEventos();
      const list = Array.isArray(data) ? data : data?.data ?? data?.noticiasEventos ?? data?.noticias ?? [];
      setItems(list);
    } catch (error) {
      console.error("Error al cargar noticias y eventos", error);
      setItems([]);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchItems();
  }, []);

  const handleEditClick = (e, item) => {
    e.preventDefault();
    e.stopPropagation();
    const id = getNoticiaEventoId(item);
    if (id != null && id !== "") {
      navigate("/noticias-eventos/editar/" + encodeURIComponent(String(id)), { state: { noticiaEvento: item } });
    } else {
      Swal.fire("Aviso", "No se pudo obtener el ID del registro.", "info");
    }
  };

  const handleMarcarBorrado = async (item) => {
    const id = getNoticiaEventoId(item);
    if (id == null) return;

    const result = await Swal.fire({
      title: "¿Marcar como borrado?",
      text: "El registro no se mostrará como publicado. Puedes editarlo después para reactivarlo.",
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#3085d6",
      cancelButtonColor: "#d33",
      confirmButtonText: "Sí, marcar borrado",
      cancelButtonText: "Cancelar",
    });

    if (!result.isConfirmed) return;

    try {
      const payload = {
        Titulo: item.Titulo ?? "",
        Resumen: item.Resumen ?? "",
        Contenido: item.Contenido ?? "",
        ImagenUrl: item.ImagenUrl ?? item.imagenUrl ?? "",
        Tipo: item.Tipo ?? "Noticia",
        CategoriaId: Number(item.CategoriaId) || 0,
        FechaEventoInicio: item.FechaEventoInicio ?? null,
        FechaEventoFin: item.FechaEventoFin ?? null,
        Ubicacion: item.Ubicacion ?? "",
        Modalidad: item.Modalidad ?? "Presencial",
        EnlaceEvento: item.EnlaceEvento ?? "",
        FechaPublicacion: item.FechaPublicacion ?? null,
        FechaExpiracion: item.FechaExpiracion ?? null,
        EsDestacado: Number(item.EsDestacado) === 1 ? 1 : 0,
        EsPublico: Number(item.EsPublico) === 1 ? 1 : 0,
        Estado: "Borrado",
      };
      await updateNoticiaEvento(id, payload);
      await fetchItems();
      Swal.fire("Listo", "Se ha marcado como borrado.", "success");
    } catch (error) {
      const msg = error?.response?.data?.message || error?.message || "No se pudo actualizar.";
      Swal.fire("Error", msg, "error");
    }
  };

  const filtered = items.filter((it) => {
    const s = search.toLowerCase();
    return (
      (it.Titulo ?? "").toLowerCase().includes(s) ||
      (it.Resumen ?? "").toLowerCase().includes(s) ||
      (it.Tipo ?? "").toLowerCase().includes(s) ||
      (it.Estado ?? "").toLowerCase().includes(s)
    );
  });

  const paginated = filtered.slice((page - 1) * rowsPerPage, page * rowsPerPage);
  const totalPages = Math.max(1, Math.ceil(filtered.length / rowsPerPage));

  const chipSx = (estado) => {
    if (estado === "Publicado")
      return { borderColor: "success.main", color: "success.dark", bgcolor: "success.light", "& .MuiChip-label": { fontWeight: 500 } };
    if (estado === "Borrado")
      return { bgcolor: "grey.100", color: "grey.600", borderColor: "grey.400", "& .MuiChip-label": { fontWeight: 500 } };
    return { borderColor: "warning.main", color: "warning.dark", bgcolor: "warning.light", "& .MuiChip-label": { fontWeight: 500 } };
  };

  return (
    <Box mb={4}>
      <Box display="flex" alignItems="center" gap={1} mb={1}>
        <ArticleIcon color="primary" />
        <Typography variant="h6">Noticias y eventos creados</Typography>
      </Box>

      <Box display="flex" justifyContent="flex-end" mb={2}>
        <TextField
          size="small"
          placeholder="Buscar por título, resumen, tipo..."
          value={search}
          onChange={(e) => { setSearch(e.target.value); setPage(1); }}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon />
              </InputAdornment>
            ),
          }}
        />
      </Box>

      <TableContainer
        component={Paper}
        sx={{ backgroundColor: "white", borderRadius: 2, boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.1)" }}
      >
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>#</TableCell>
              <TableCell>Título</TableCell>
              <TableCell>Tipo</TableCell>
              <TableCell>Resumen</TableCell>
              <TableCell>Fecha publicación</TableCell>
              <TableCell>Estado</TableCell>
              <TableCell>Destacado</TableCell>
              <TableCell align="right">Acciones</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {loading ? (
              <TableRow>
                <TableCell colSpan={8} align="center"><Typography color="text.secondary">Cargando...</Typography></TableCell>
              </TableRow>
            ) : paginated.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center"><Typography color="text.secondary">No hay noticias ni eventos registrados.</Typography></TableCell>
              </TableRow>
            ) : (
              paginated.map((it, index) => (
                <TableRow key={getNoticiaEventoId(it) ?? index}>
                  <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                  <TableCell sx={{ maxWidth: 180 }}>{it.Titulo || "—"}</TableCell>
                  <TableCell>{it.Tipo || "—"}</TableCell>
                  <TableCell sx={{ maxWidth: 200 }}>
                    <Typography noWrap title={it.Resumen || ""}>
                      {it.Resumen ? (it.Resumen.length > 35 ? `${it.Resumen.slice(0, 35)}…` : it.Resumen) : "—"}
                    </Typography>
                  </TableCell>
                  <TableCell>{it.FechaPublicacion ? String(it.FechaPublicacion).slice(0, 10) : "—"}</TableCell>
                  <TableCell>
                    <Chip
                      label={estadoLabel(it.Estado)}
                      size="small"
                      variant="outlined"
                      sx={chipSx(it.Estado)}
                    />
                  </TableCell>
                  <TableCell>{Number(it.EsDestacado) === 1 ? "Sí" : "No"}</TableCell>
                  <TableCell align="right">
                    <Tooltip title="Editar">
                      <IconButton type="button" color="primary" size="small" onClick={(e) => handleEditClick(e, it)} aria-label="Editar">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Marcar como borrado">
                      <IconButton
                        color="primary"
                        size="small"
                        onClick={() => handleMarcarBorrado(it)}
                        disabled={it.Estado === "Borrado"}
                      >
                        <BlockIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {totalPages > 1 && (
        <Box display="flex" justifyContent="center" mt={2}>
          <Pagination count={totalPages} page={page} onChange={(e, value) => setPage(value)} color="primary" />
        </Box>
      )}
    </Box>
  );
}
